import React, { useState } from 'react';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import {
    SP_SKIP,
    extractResponseData,
    SmartHeader,
    SmartField,
    NoRecordsCard,
    RawResponseButton,
    prettyLabel,
    isScalar,
    statusSeverity,
} from './_SmartFieldRenderer';

const FIELD_GROUPS = {
    searchInfo: ['name', 'father_name', 'husband_name', 'mother_name', 'cnr_number', 'search_type', 'case_type', 'category', 'source', 'year', 'state', 'state_name', 'district', 'address', 'address_state'],
    caseDetails: ['case_number', 'case_no', 'case_title', 'case_year', 'case_status', 'case_stage', 'filing_number', 'registration_number', 'cino', 'case_type_name', 'disposal_nature', 'nature_of_disposal'],
    court: ['court_name', 'court_id', 'court_complex', 'court_establishment', 'court_number', 'judge_name', 'judge_senior_division', 'judge_junior_division', 'bench'],
    parties: ['petitioner_name', 'respondent_name', 'petitioner', 'respondent', 'petitioner_advocate', 'respondent_advocate', 'advocate_name'],
    dates: ['filing_date', 'registration_date', 'first_hearing_date', 'last_hearing_date', 'next_hearing_date', 'decision_date', 'disposal_date'],
    legal: ['acts', 'sections', 'subject', 'category_details', 'charges', 'offence'],
};

const GROUP_META = {
    searchInfo: { title: 'Search Criteria', icon: 'pi-search' },
    caseDetails: { title: 'Case Details', icon: 'pi-folder' },
    court: { title: 'Court & Judge', icon: 'pi-building' },
    parties: { title: 'Parties', icon: 'pi-users' },
    dates: { title: 'Key Dates', icon: 'pi-calendar' },
    legal: { title: 'Legal Subject', icon: 'pi-book' },
};

const isCaseArray = (v) =>
    Array.isArray(v) &&
    v.length > 0 &&
    typeof v[0] === 'object' &&
    (v[0].case_number || v[0].cnr_number || v[0].case_title || v[0].case_no || v[0].cino);

const Section = ({ icon, title, children }) => (
    <div className="pt-3 pb-3 border-bottom-1 surface-border">
        <div className="flex align-items-center gap-2 mb-3">
            <i className={`pi ${icon} text-primary`} aria-hidden="true" />
            <span className="text-sm font-semibold text-900 uppercase">{title}</span>
        </div>
        {children}
    </div>
);

const CaseCard = ({ data, onClick }) => (
    <div
        className="col-12 md:col-6"
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
        style={{ cursor: 'pointer' }}
    >
        <div className="surface-100 border-round border-left-3 border-primary p-3 h-full">
            <div className="flex justify-content-between align-items-start mb-2 gap-2">
                <span className="text-primary text-sm font-bold font-mono">
                    {data.cnr_number || data.case_number || data.case_no || data.cino || '—'}
                </span>
                {(data.case_status || data.status) && (
                    <Tag value={data.case_status || data.status} severity={statusSeverity(data.case_status || data.status)} />
                )}
            </div>
            {(data.case_title || data.petitioner_name) && (
                <div className="text-900 text-sm font-semibold mb-1">{data.case_title || data.petitioner_name}</div>
            )}
            {data.court_name && (
                <div className="text-500 text-xs mb-1">
                    <i className="pi pi-building mr-1" aria-hidden="true" />
                    {data.court_name}
                </div>
            )}
            {(data.filing_date || data.registration_date) && (
                <div className="text-500 text-xs">
                    <i className="pi pi-calendar mr-1" aria-hidden="true" />
                    {data.filing_date || data.registration_date}
                </div>
            )}
            <div className="text-primary text-xs mt-2">View details &rarr;</div>
        </div>
    </div>
);

const EcourtsOutputComponent = ({ reportData, hideCredits, isSharedView }) => {
    const shouldHideCredits = hideCredits || isSharedView;
    const [selectedCase, setSelectedCase] = useState(null);
    const { responseData, remainingBalance, errorMsg, hasRealData } = extractResponseData(reportData);

    if (!reportData) return <NoRecordsCard subtitle="No data available" />;
    if (!responseData || !hasRealData) {
        return <NoRecordsCard errorMsg={errorMsg} subtitle="No court records found for the provided details. Please verify and try again." />;
    }

    const d = responseData;

    const caseArrayKeys = Object.keys(d).filter((k) => isCaseArray(d[k]));
    const preferredOrder = ['cases', 'case_details', 'results', 'search_results'];
    const primaryKey = preferredOrder.find((k) => caseArrayKeys.includes(k)) || caseArrayKeys[0];
    const primaryArray = primaryKey ? d[primaryKey] : [];

    const seen = new Set();
    const allCases = [];
    for (const c of primaryArray) {
        const key = c.cnr_number || c.case_number || c.case_no || c.cino || JSON.stringify(c);
        if (seen.has(key)) continue;
        seen.add(key);
        allCases.push(c);
    }

    const allKeys = Object.keys(d).filter((k) => !SP_SKIP.has(k));
    const grouped = {};
    const unknownScalars = [];
    const unknownObjects = [];
    const unknownArrays = [];

    for (const k of allKeys) {
        const v = d[k];
        if (caseArrayKeys.includes(k)) continue;

        let placed = false;
        for (const [groupName, fields] of Object.entries(FIELD_GROUPS)) {
            if (fields.includes(k)) {
                grouped[groupName] = grouped[groupName] || [];
                grouped[groupName].push([k, v]);
                placed = true;
                break;
            }
        }
        if (placed) continue;

        if (isScalar(v)) unknownScalars.push([k, v]);
        else if (Array.isArray(v)) {
            if (v.length > 0) unknownArrays.push([k, v]);
        } else if (typeof v === 'object' && v !== null && Object.keys(v).length > 0) {
            unknownObjects.push([k, v]);
        }
    }

    return (
        <div>
            <SmartHeader title="eCourts Search Report" icon="pi-file" remainingBalance={remainingBalance} shouldHideCredits={shouldHideCredits} />

            <Card>
                {['searchInfo', 'caseDetails', 'court', 'parties', 'dates', 'legal'].map((groupName) => {
                    const entries = grouped[groupName];
                    if (!entries || entries.length === 0) return null;
                    const meta = GROUP_META[groupName];
                    return (
                        <Section key={groupName} icon={meta.icon} title={meta.title}>
                            <div className="grid">
                                {entries.map(([k, v]) => (
                                    <SmartField key={k} name={k} value={v} highlight={/cnr|number/i.test(k)} />
                                ))}
                            </div>
                        </Section>
                    );
                })}

                {allCases.length > 0 && (
                    <Section icon="pi-list" title={`Cases Found (${allCases.length})`}>
                        <div className="grid">
                            {allCases.map((c, idx) => (
                                <CaseCard key={idx} data={c} onClick={() => setSelectedCase(c)} />
                            ))}
                        </div>
                    </Section>
                )}

                {unknownArrays.map(([k, v]) => {
                    const isObjectArray = typeof v[0] === 'object' && v[0] !== null;
                    return (
                        <Section key={k} icon="pi-list" title={`${prettyLabel(k)} (${v.length})`}>
                            {isObjectArray ? (
                                <DataTable
                                    value={v}
                                    size="small"
                                    stripedRows
                                    showGridlines
                                    responsiveLayout="scroll"
                                    paginator={v.length > 10}
                                    rows={10}
                                    className="p-datatable-sm p-datatable-gridlines"
                                >
                                    {Object.keys(v[0])
                                        .filter((key) => isScalar(v[0][key]))
                                        .map((key) => (
                                            <Column key={key} field={key} header={prettyLabel(key)} />
                                        ))}
                                </DataTable>
                            ) : (
                                <div className="grid">
                                    {v.map((item, i) => (
                                        <SmartField key={i} name={`item_${i + 1}`} value={String(item)} />
                                    ))}
                                </div>
                            )}
                        </Section>
                    );
                })}

                {unknownObjects.map(([k, v]) => (
                    <Section key={k} icon="pi-folder" title={prettyLabel(k)}>
                        <div className="grid">
                            {Object.entries(v)
                                .filter(([, val]) => isScalar(val) && val !== null && val !== undefined && val !== '')
                                .map(([ik, iv]) => (
                                    <SmartField key={ik} name={ik} value={iv} />
                                ))}
                        </div>
                    </Section>
                ))}

                {unknownScalars.length > 0 && (
                    <Section icon="pi-info-circle" title="Other Details">
                        <div className="grid">
                            {unknownScalars.map(([k, v]) => (
                                <SmartField key={k} name={k} value={v} />
                            ))}
                        </div>
                    </Section>
                )}

                <RawResponseButton data={d} />
            </Card>

            <Dialog
                header="Case Details"
                visible={!!selectedCase}
                onHide={() => setSelectedCase(null)}
                style={{ width: '90vw', maxWidth: '900px' }}
                modal
                draggable={false}
            >
                {selectedCase && (
                    <div className="grid">
                        {Object.entries(selectedCase)
                            .filter(([k]) => !SP_SKIP.has(k))
                            .map(([k, v]) => {
                                if (v === null || v === undefined || v === '') return null;
                                if (isScalar(v)) return <SmartField key={k} name={k} value={v} />;
                                if (Array.isArray(v) && v.length > 0) {
                                    return (
                                        <div key={k} className="col-12 flex flex-column gap-1">
                                            <span className="text-500 text-xs font-medium">{prettyLabel(k)}</span>
                                            <pre className="surface-100 border-round text-700 text-xs p-3 m-0" style={{ maxHeight: '200px', overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                                                {JSON.stringify(v, null, 2)}
                                            </pre>
                                        </div>
                                    );
                                }
                                if (typeof v === 'object' && Object.keys(v).length > 0) {
                                    return (
                                        <div key={k} className="col-12">
                                            <span className="text-500 text-xs font-medium">{prettyLabel(k)}</span>
                                            <div className="surface-100 border-round p-3 mt-1">
                                                <div className="grid">
                                                    {Object.entries(v)
                                                        .filter(([, iv]) => isScalar(iv) && iv !== null && iv !== undefined && iv !== '')
                                                        .map(([ik, iv]) => (
                                                            <SmartField key={ik} name={ik} value={iv} />
                                                        ))}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                                return null;
                            })}
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default EcourtsOutputComponent;
